import { format } from 'date-fns'
import React, { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { match, P } from 'ts-pattern'

import Cross from '@/components-latest/primitives/Icons/Cross'
import Widget from '@/components-latest/primitives/Widget'
import {
  resolveStoreBrand,
  STORE_BRAND_LABEL_KEYS
} from '@/ducks/orders/helpers/brand.helpers'
import { OrderFilters } from '@/ducks/orders/slices/orders.slice'
import { selectCurrencyCode } from '@/ducks/store/selectors/store.selectors'

export const FilterWidgets: React.FC<{
  filters: OrderFilters
  onClick: (name: string | string[]) => void
}> = ({ filters, onClick }) => {
  const { t } = useTranslation()

  const currency = useSelector(selectCurrencyCode)

  const formatDate = (date: string) => format(new Date(date), 'MMM dd, yyyy')

  const getWidgetProps = useCallback(
    (name: string) =>
      match({
        start_date: null,
        end_date: null,
        start_price: null,
        end_price: null,
        ...filters,
        name
      })
        .with(
          {
            name: 'start_date',
            start_date: P.string,
            end_date: P.nullish
          },
          ({ start_date }) => {
            return {
              children: t('orders.filters.widgets.start-date', {
                date: formatDate(start_date)
              }),
              onClick: () => onClick(name)
            }
          }
        )
        .with(
          {
            name: 'end_date',
            start_date: P.nullish,
            end_date: P.string
          },
          ({ end_date }) => ({
            children: t('orders.filters.widgets.end-date', {
              date: formatDate(end_date)
            }),
            onClick: () => onClick(name)
          })
        )
        .with(
          {
            name: 'end_date',
            start_date: P.string,
            end_date: P.string
          },
          ({ end_date, start_date }) => ({
            children: t('orders.filters.widgets.range-date', {
              startDate: formatDate(start_date),
              endDate: formatDate(end_date)
            }),
            onClick: () => onClick(['start_date', 'end_date'])
          })
        )
        .with(
          {
            name: 'start_price',
            start_price: P.number,
            end_price: P.nullish
          },
          () => ({
            children: t('orders.filters.widgets.start-price', {
              price: filters.start_price,
              currency
            }),
            onClick: () => onClick(name)
          })
        )
        .with(
          {
            name: 'end_price',
            start_price: P.nullish,
            end_price: P.number
          },
          () => ({
            children: t('orders.filters.widgets.end-price', {
              price: filters.end_price,
              currency
            }),
            onClick: () => onClick(name)
          })
        )
        .with(
          {
            name: 'end_price',
            start_price: P.number,
            end_price: P.number
          },
          () => ({
            children: t('orders.filters.widgets.range-price', {
              startPrice: filters.start_price,
              endPrice: filters.end_price,
              currency
            }),
            onClick: () => onClick(['end_price', 'start_price'])
          })
        )
        .with(
          {
            name: 'brand',
            brand: P.string
          },
          ({ brand }) => ({
            children: t(STORE_BRAND_LABEL_KEYS[resolveStoreBrand(brand)]),
            onClick: () => onClick(name)
          })
        )
        .otherwise(() => null),
    [currency, filters, onClick, t]
  )

  return (
    <>
      {Object.entries(filters).reduce((acc, [key]) => {
        if (
          key === 'statuses' &&
          filters.statuses &&
          filters.statuses.length > 0
        ) {
          return [
            ...acc,
            ...filters['statuses'].map(status => (
              <Widget
                key={`${key}-${status}`}
                onClick={() => onClick(status)}
                data-testid={`filter-widget-${status}`}>
                {t(
                  `orders.statuses.${status.toLowerCase().replace(/_/g, '-')}`
                )}
                <Cross />
              </Widget>
            ))
          ]
        }

        const widgetProps = getWidgetProps(key)
        if (widgetProps)
          return [
            ...acc,
            <Widget
              key={key}
              {...widgetProps}
              data-testid={`filter-widget-${key}`}>
              {widgetProps.children}
              <Cross />
            </Widget>
          ]

        return acc
      }, [] as React.ReactNode[])}
    </>
  )
}
